<template>
  <div class="add-ons__certifications" v-if="certificationsList.length">
    <div
      :class="[
        'add-ons__certifications-wrapper',
        certificationsToggler && 'add-ons__certifications-wrapper--show',
      ]"
    >
      <div
        @click="certificationsToggler = !certificationsToggler"
        :class="[
          'add-ons__certifications-placeholder',
          certificationsToggler && 'add-ons__certifications-placeholder--show',
        ]"
      >
        <div class="add-ons__certifications-placeholder-text">Certifications</div>
        <Pill infoWhite v-if="selectedItems > 0"> {{ selectedItems }} selected </Pill>
        <ChevronDownIcon class="add-ons__chevron" />
      </div>
      <div
        v-show="certificationsToggler"
        :class="[
          'add-ons__certifications-items-wrapper',
          certificationsToggler && 'add-ons__certifications-items-wrapper--show',
        ]"
      >
        <div
          class="add-ons__certifications-item"
          v-for="certification in certificationsList"
          :key="certification.merchant_id"
          @click="handleItemRowClick(certification)"
        >
          <div class="add-ons__certifications-item-name">
            <div class="add-ons__certifications-item-name-text">
              {{ certification.name }}
            </div>
            <div class="add-ons__certifications-item-name-included" v-if="certification.included">
              Free with this subscription
            </div>
            <div
              class="add-ons__certifications-item-name-text--sub"
              v-if="showDiscountLabel(certification)"
            >
              Discount shown
            </div>
          </div>
          <div class="add-ons__certifications-item-price">
            <span>{{ priceToShow(certification).units }}</span>
            <span
              v-if="priceToShow(certification).cents"
              class="add-ons__certifications-item-price--cents"
              >{{ priceToShow(certification).cents }}</span
            >
          </div>
          <div class="add-ons__certifications-item-check">
            <CheckGreen
              class="add-ons__certifications-item-check-included"
              v-if="certification.included"
            />
            <CheckBox
              v-else
              :cartTheme="true"
              :isSelected="certification.addOnChecked"
              @click="updateSelectedCertification(certification)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import CheckGreenImg from '@/assets/icons/check_green.svg'
import CheckBox from '@/components/shared/CheckBox.vue'
import Pill from '@/components/shared/Pill.vue'
import ChevronDownIcon from '@/assets/icons/chevron_down.svg'
import CONSTANTS from '@/constants'
import { cloneDeep } from 'lodash'
import { updateCart, routesRules } from '@/helpers/mixins'

const {
  PLANS,
  PREMIUM_PLANS,
  VOUCHERS,
  LEAP_YEAR_PROMO_PRODUCT_BUNDLES,
  TCS_PROMO_PRODUCT_BUNDLES,
} = CONSTANTS

const { INE_TCS_BUNDLE } = TCS_PROMO_PRODUCT_BUNDLES

const VOUCHERS_PLUS_SUBSCRIPTIONS = [
  VOUCHERS.EJPT,
  VOUCHERS.EDA,
  VOUCHERS.EWPT,
  VOUCHERS.ECPPT3,
  VOUCHERS.EWPTX,
]

export default {
  name: 'AddOnsListCertifications',
  mixins: [updateCart, routesRules],
  components: {
    CheckBox,
    Pill,
    CheckGreen: { ...CheckGreenImg, name: 'CheckGreenImg' },
    ChevronDownIcon: { ...ChevronDownIcon, name: 'ChevronDownIcon' },
  },
  data() {
    return {
      filteredItems: [],
      certifications: [],
      certificationsList: [],
      certificationsToggler: false,
      selectedItems: 0,
      ICCAVoucherCode: VOUCHERS.ICCA,
      fundamentalsAnnualOnCart: false,
    }
  },
  props: {
    bundleCertificationSelected: {
      type: String,
      required: false,
      default: null,
    },
  },
  computed: {
    ...mapGetters({
      usersCurrentSubscriptions: 'subscriptions/getUsersCurrentSubscriptions',
      selectedPlanCode: 'shoppingCart/selectedPlanCode',
      shoppingCart: 'shoppingCart/getShoppingCart',
      isBusinessUser: 'business/isBusinessUser',
      isPromotionActive: 'app/isPromotionActive',
      isFlashSaleActive: 'featureFlags/isFlashSaleActive',
      availableCertifications: 'products/availableCertifications',
      addOnscertificationsList: 'shoppingCart/getAddOnsCertificationsList',
      cartHasPremiumSubscription: 'shoppingCart/cartHasPremiumSubscription',
    }),
    cartHasSubscription() {
      const itemsId = this.shoppingCart.map(item => item.merchant_id)

      const allPlansExceptSkillDive = Object.values(PLANS).filter(
        code => ![PLANS.SKILL_DIVE_ANNUAL, PLANS.SKILL_DIVE_PTA].includes(code)
      )

      return itemsId.some(code =>
        [
          ...allPlansExceptSkillDive,
          ...Object.values(LEAP_YEAR_PROMO_PRODUCT_BUNDLES),
          INE_TCS_BUNDLE,
        ].includes(code)
      )
    },
    userHasSubcription() {
      return this.usersCurrentSubscriptions.length > 0
    },
    userHasFundamentalsSubscription() {
      const userHasFundamentals = !!this.usersCurrentSubscriptions.find(subscription =>
        [PLANS.MONTHLY, PLANS.ANNUAL].includes(subscription.plan_code)
      )
      return this.usersCurrentSubscriptions.length > 0 && userHasFundamentals
    },
    cartHasOnlyAddOnsVouchers() {
      const certsOnCart = this.shoppingCart.filter(
        item => Object.values(VOUCHERS).includes(item.merchant_id) && !item.addOn
      )
      return certsOnCart.length
    },
  },
  created() {
    this.initCertifications()
    this.setCertificationsList()
    this.updateSelectedItemsPill()
  },
  watch: {
    cartHasSubscription: {
      handler() {
        this.initCertifications()
        this.setCertificationsList()
      },
    },
    bundleCertificationSelected: 'setCertificationsList',
    cartHasOnlyAddOnsVouchers: {
      handler() {
        this.initCertifications()
        this.setCertificationsList()
      },
    },
  },
  methods: {
    ...mapActions({
      setAddOnscertificationsList: 'shoppingCart/setAddOnscertificationsList',
    }),
    updateSelectedItemsPill() {
      this.selectedItems = this.certificationsList.filter(cert => cert.addOnChecked).length
    },
    updateSelectedCertification(certification) {
      const exists = this.shoppingCart.find(item => item.id === certification.id)
      if (exists) {
        certification.addOn = false
        certification.addOnChecked = false
        this.removeProductFromCart(certification)
      } else {
        certification.addOn = true
        certification.addOnChecked = true
        this.addProductToCart(certification)
      }
      this.updateSelectedItemsPill()
    },
    handleItemRowClick(certification) {
      if (!certification.included) this.updateSelectedCertification(certification)
    },
    setIncludedCertification() {
      this.certifications.forEach(cert => {
        cert.included = cert.includedWith && cert.includedWith.includes(this.selectedPlanCode)
        if (cert.included) {
          this.removeProductFromCart(cert)
        }
      })
    },
    filterVouchersOnCart() {
      // filteredItems is a list of items.merchant_id that should not be shown on the list
      // if a voucher is on the cart already, it should not be shown on the list
      this.filteredItems = this.shoppingCart.map(item => {
        if (item.merchant_id === PLANS.ANNUAL) {
          this.fundamentalsAnnualOnCart = true
        }
        return item.merchant_id
      })
    },
    filterVouchersNoSubscriptionOnCart() {
      this.filteredItems.push(...VOUCHERS_PLUS_SUBSCRIPTIONS)
      // remove VOUCHERS if cart Has NO Subscription
      // removed vochers EJPT, EDA, EWPT, ECPPT3, EWPTX
      const vouchersWithSubscription = this.filteredItems.some(i =>
        VOUCHERS_PLUS_SUBSCRIPTIONS.includes(i)
      )
      if (vouchersWithSubscription) {
        this.shoppingCart.forEach(item => {
          if (VOUCHERS_PLUS_SUBSCRIPTIONS.includes(item.merchant_id)) {
            item.addOn = false
            item.addOnChecked = false
            this.removeProductFromCart(item)
            // Add voucher merchant_id to the filteredItems list
            // to exclude it from the add-ons list
            this.filteredItems.push(item.merchant_id)
          }
        })
      }
      const cartHasMonthlyEjpt = this.filteredItems.includes([
        PLANS.MONTHLY_EJPT,
        PLANS.EJPT_FUNDAMENTALS,
      ])
      if (cartHasMonthlyEjpt)
        this.filteredItems.splice(this.filteredItems.indexOf(VOUCHERS.EJPT), 1)
    },
    applyPromotion() {
      // if cart has promotional subscription and promotion is active
      // add discount to the certifications
      if (this.isPromotionActive) {
        this.certifications.forEach(certification => {
          if (VOUCHERS_PLUS_SUBSCRIPTIONS.includes(certification.merchant_id)) {
            certification.user_receives_discount = this.filteredItems.some(itemCode =>
              [...Object.values(PREMIUM_PLANS)].includes(itemCode)
            )
          }
        })
      }
    },
    filterVouchersBundleOnCart() {
      // remove bundleCertificationSelected from the list to avoid duplication on the cart
      if (this.bundleCertificationSelected) {
        this.filteredItems.push(this.bundleCertificationSelected)
        const certOnBundle = this.certifications.find(
          cert => cert.merchant_id === this.bundleCertificationSelected
        )
        // clear certification from the add on list
        if (certOnBundle) {
          certOnBundle.addOn = false
          certOnBundle.addOnChecked = false
        }
      }
    },
    addOnsConsistent() {
      // if voucher was added to cart as AddOn, it should remain as an AddOn
      const vouchersOnCart = this.filteredItems.some(code =>
        Object.values({ ...VOUCHERS }).includes(code)
      )
      if (vouchersOnCart) {
        this.certifications.forEach(item => {
          if (item.addOn) {
            this.filteredItems.splice(this.filteredItems.indexOf(item.merchant_id), 1)
          }
        })
      }
    },
    updateCertificationsList() {
      this.certificationsList = this.certifications.filter(item => {
        return !this.filteredItems.includes(item.merchant_id)
      })
      this.setAddOnscertificationsList(this.certificationsList)
    },
    setCertificationsList() {
      this.setIncludedCertification()
      if (this.userHasSubcription || this.isBusinessUser || this.isFlashSaleActive) {
        this.filterVouchersOnCart()
        this.updateCertificationsList()
        return
      }

      this.filterVouchersOnCart()
      if (!this.cartHasSubscription) {
        this.filterVouchersNoSubscriptionOnCart()
      }
      this.applyPromotion()
      this.filterVouchersBundleOnCart()
      this.addOnsConsistent()
      this.updateCertificationsList()
      this.updateSelectedItemsPill()
    },
    priceToShow(certification) {
      if (certification.included) {
        return 'Free'
      }
      let newPrice
      if (this.isPromotionActive) {
        newPrice = certification.user_receives_discount
          ? `$${certification.discounted_price_cents / 100}`
          : `$${certification.price_cents / 100}`
        newPrice = newPrice.split('.')
      } else {
        newPrice = certification.price.split('.')
      }
      return {
        units: newPrice[0],
        cents: newPrice[1] > 0 ? `.${newPrice[1] < 10 ? newPrice[1] * 10 : newPrice[1]}` : false,
      }
    },
    showDiscountLabel(certification) {
      return (
        this.isPromotionActive &&
        !certification.included &&
        certification.merchant_id !== this.ICCAVoucherCode &&
        certification.user_receives_discount
      )
    },
    newListOldState() {
      const vertListAux = cloneDeep(this.availableCertifications).reverse()
      this.certifications = vertListAux.map(cert => {
        const existingCert =
          this.addOnscertificationsList.length > 0 &&
          this.addOnscertificationsList.find(
            addOnCert => addOnCert.merchant_id === cert.merchant_id
          )

        return existingCert || cert
      })
    },
    initCertifications() {
      if (this.cartHasSubscription || (!this.cartHasSubscription && this.shoppingCart.length > 0)) {
        this.newListOldState()
      } else {
        this.certifications =
          this.addOnscertificationsList.length > 0
            ? this.addOnscertificationsList
            : cloneDeep(this.availableCertifications).reverse()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.add-ons {
  &__certifications {
    cursor: pointer;
    &-wrapper {
      height: 55px;
      background: var(--gray-3);
      border-radius: 20px;
      transition: 0.3s all ease-in-out;
      &--show {
        height: 100%;
      }
    }
    &-placeholder {
      height: 55px;
      border-radius: 20px;
      background: linear-gradient(90deg, rgba(51, 51, 51, 1) 60%);
      background-position: center right;
      background-image: var(--certs-addons-svg-bg);
      background-repeat: no-repeat;
      display: flex;
      padding: 5px 30px;
      align-items: center;
      font-weight: 700;
      justify-content: space-between;
      gap: 10px;
      font-size: rem(16px);
      line-height: rem(23px);
      @include tablet {
        font-size: rem(20px);
        line-height: rem(26px);
      }
      .pill {
        white-space: nowrap;
        margin-left: auto;
      }
      &--show {
        border-radius: 20px 20px 0 0;
      }
    }
    &-items {
      display: flex;
      flex-flow: column;
      row-gap: 10px;
      &-wrapper {
        width: 100%;
        height: 0px;
        transition: 0.2s all ease-in-out;
        opacity: 0;
        padding: 20px;
        display: flex;
        flex-flow: column;
        gap: 5px;
        z-index: -1;
        position: relative;
        &--show {
          opacity: 1;
          height: 100%;
          cursor: auto;
          z-index: 1;
        }
      }
    }
    &-item {
      display: grid;
      grid-template-columns: auto 70px 50px;
      padding: 10px;
      border-radius: 10px;
      align-items: center;
      background: var(--glass-mid-gray);
      height: 46px;
      align-content: center;
      cursor: pointer;
      &:hover {
        background: var(--glass-extra-light);
      }
      &-check {
        display: flex;
        justify-content: center;
        .check-box {
          display: flex;
          align-items: center;
        }
        &-included {
          margin: 0 auto;
        }
      }
      &-name {
        font-weight: 500;
        font-size: rem(14px);
        line-height: rem(20px);
        &-included {
          font-weight: 450;
          font-size: rem(12px);
          line-height: rem(14px);
          color: var(--strong-light-green-b5);
          margin-top: 5px;
        }
        &-text {
          &--sub {
            color: var(--light-blue-gray, #afb0bf);
            font-size: rem(12px);
            font-style: normal;
            font-weight: 450;
            line-height: rem(14px);
          }
        }
      }
      &-price {
        text-align: right;
        font-size: rem(20px);
        font-weight: 700;
        line-height: rem(26px);
        &--cents {
          vertical-align: super;
          font-size: rem(10px);
          margin: 10px 0 0 3px;
        }
      }
    }
  }
}
</style>
